import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";

import type { AuthStore } from "@/Modules/Auth/Store";

import { isIosApp } from "@/Utils/Ionic";
import { getTokenExpirationSeconds } from "@/Utils/JWT";
import { isValidString } from "@/Utils/String";

interface AuthRetryConfig extends InternalAxiosRequestConfig {
  authRetry?: boolean;
}

const isAuthUrl = (url: string) => {
  return ["auth/", "users/login", "users/logout", "passwords/"].some((path) => url.includes(path));
};

// undefined: cerrar sesión; null: no reintentar ni cerrar aquí; respuesta: petición recuperada.
async function retryUnauthorizedRequest(
  client: AxiosInstance,
  error: AxiosError,
  authStore: AuthStore
): Promise<AxiosResponse | null | undefined> {
  const request = error.config as AuthRetryConfig | undefined;
  const token = authStore.userToken;

  if (request && isAuthUrl(request.url ?? "")) {
    return null;
  }

  if (!request || isIosApp()) {
    return undefined;
  }

  if (!isValidString(token)) {
    return null;
  }

  if (request.headers.get("Authorization") !== `Bearer ${token}`) {
    return null;
  }

  if (request.authRetry) {
    return undefined;
  }

  const expiration = getTokenExpirationSeconds(token);

  if (expiration === undefined || expiration > Math.floor(Date.now() / 1000)) {
    return undefined;
  }

  request.authRetry = true;

  const refreshed = await authStore.refreshToken();

  if (!refreshed) {
    return null;
  }

  if (!isValidString(authStore.userToken)) {
    return null;
  }

  request.headers.set("Authorization", `Bearer ${authStore.userToken}`);

  return client(request);
}

export default retryUnauthorizedRequest;
